import { useMemo, useState } from 'react'
import { SegmentProcessResult } from '../types'
import { formatRoiValue } from '../utils/roi-copy'

// ─── Types ───────────────────────────────────────────────────────────────────

interface ExplorerCompany {
  id: string
  name: string
  plan: string
  segment: string
  // core
  roi: number | null
  roiBay: number | null
  revenue: number
  revInfluenced: number
  orders: number
  influenced: number
  pctInfluenced: number
  cost: number
  ticket: number
  conv: number
  anomaly: boolean
  // vc
  sessionsWithVideo: number
  sessionsNoVideo: number
  bounceWithVideo: number
  avgDurationVideo: number
  pagesPerSession: number
  activeVideos: number
  // lc (null when company has no LC)
  hasLC: boolean
  nLives: number | null
  peakConcurrent: number | null
  avgWatchMin: number | null
  convLive: number | null
  revenuePerLive: number | null
  matchGARate: number | null
  uniqViewersLive: number | null
  reactionsLive: number | null
  chatMsgsLive: number | null
}

type FieldType = 'num' | 'str' | 'bool'

interface FieldDef {
  id: string
  label: string
  type: FieldType
  unit?: string
  group: 'core' | 'vc' | 'lc' | 'dim'
  values?: string[]
  fmt: (v: number) => string
}

interface FilterDef {
  field: string
  op: string
  value: number | string | boolean
}

type Slot = 'x' | 'y' | 'color'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const seed = (s: string): number => {
  let h = 0
  for (const c of s) h = (h * 31 + c.charCodeAt(0)) & 0x7fffffff
  return h
}
const rng = (s: string, lo: number, hi: number): number => {
  const r = (seed(s) % 1000) / 1000
  return lo + r * (hi - lo)
}

const fmtBRL = (v: number) =>
  v >= 1_000_000
    ? `R$ ${(v / 1_000_000).toFixed(1)}M`
    : v >= 1_000
    ? `R$ ${(v / 1_000).toFixed(0)}k`
    : `R$ ${v.toFixed(0)}`

const fmtNum = (v: number) =>
  v >= 1_000 ? `${(v / 1_000).toFixed(1)}k` : String(Math.round(v))

// ─── Field catalog ────────────────────────────────────────────────────────────

const FIELD_GROUPS: Record<string, { label: string; tone: 'core' | 'vc' | 'lc' | 'dim'; items: FieldDef[] }> = {
  core: {
    label: 'Resultado',
    tone: 'core',
    items: [
      { id: 'roi',          label: 'ROI Widde',            type: 'num', unit: '×',  group: 'core', fmt: v => `${formatRoiValue(v)}×` },
      { id: 'roiBay',       label: 'ROI Bayesiano',        type: 'num', unit: '×',  group: 'core', fmt: v => `${formatRoiValue(v)}×` },
      { id: 'revenue',      label: 'Receita Total',        type: 'num', unit: 'R$', group: 'core', fmt: fmtBRL },
      { id: 'revInfluenced',label: 'Receita Influenciada', type: 'num', unit: 'R$', group: 'core', fmt: fmtBRL },
      { id: 'orders',       label: 'Pedidos',              type: 'num', group: 'core', fmt: fmtNum },
      { id: 'influenced',   label: 'Pedidos Influenciados',type: 'num', group: 'core', fmt: fmtNum },
      { id: 'pctInfluenced',label: '% Influenciada',       type: 'num', unit: '%',  group: 'core', fmt: v => `${v.toFixed(1)}%` },
      { id: 'cost',         label: 'Custo Mensal',         type: 'num', unit: 'R$', group: 'core', fmt: fmtBRL },
      { id: 'ticket',       label: 'Ticket Médio',         type: 'num', unit: 'R$', group: 'core', fmt: fmtBRL },
      { id: 'conv',         label: 'Conversão %',          type: 'num', unit: '%',  group: 'core', fmt: v => `${v.toFixed(2)}%` },
    ],
  },
  vc: {
    label: 'Video Commerce',
    tone: 'vc',
    items: [
      { id: 'sessionsWithVideo', label: 'Sessões c/ vídeo',   type: 'num', group: 'vc', fmt: fmtNum },
      { id: 'sessionsNoVideo',   label: 'Sessões s/ vídeo',   type: 'num', group: 'vc', fmt: fmtNum },
      { id: 'bounceWithVideo',   label: 'Bounce c/ vídeo',    type: 'num', group: 'vc', fmt: v => `${v.toFixed(1)}%` },
      { id: 'avgDurationVideo',  label: 'Duração Sessão (s)', type: 'num', group: 'vc', fmt: v => `${v}s` },
      { id: 'pagesPerSession',   label: 'Págs/Sessão',        type: 'num', group: 'vc', fmt: v => v.toFixed(1) },
      { id: 'activeVideos',      label: 'Vídeos Ativos',      type: 'num', group: 'vc', fmt: fmtNum },
    ],
  },
  lc: {
    label: 'Live Commerce',
    tone: 'lc',
    items: [
      { id: 'peakConcurrent',  label: 'Pico de Simultâneos',    type: 'num', group: 'lc', fmt: fmtNum },
      { id: 'avgWatchMin',     label: 'Tempo Médio na Live',    type: 'num', group: 'lc', fmt: v => `${v.toFixed(1)}min` },
      { id: 'convLive',        label: 'Conv. Durante Live',     type: 'num', group: 'lc', fmt: v => `${v.toFixed(1)}%` },
      { id: 'revenuePerLive',  label: 'Receita por Live',       type: 'num', group: 'lc', fmt: fmtBRL },
      { id: 'matchGARate',     label: 'Match GA Rate',          type: 'num', group: 'lc', fmt: v => `${v}%` },
      { id: 'uniqViewersLive', label: 'Viewers Únicos por Live',type: 'num', group: 'lc', fmt: fmtNum },
      { id: 'reactionsLive',   label: 'Reações por Live',       type: 'num', group: 'lc', fmt: fmtNum },
      { id: 'chatMsgsLive',    label: 'Msgs no Chat',           type: 'num', group: 'lc', fmt: fmtNum },
      { id: 'nLives',          label: 'Nº de Lives',            type: 'num', group: 'lc', fmt: fmtNum },
    ],
  },
  dim: {
    label: 'Dimensões',
    tone: 'dim',
    items: [
      { id: 'plan',    label: 'Plano',             type: 'str', group: 'dim', values: ['Basic', 'Pro', 'Enterprise'], fmt: v => String(v) },
      { id: 'segment', label: 'Status Assinatura', type: 'str', group: 'dim', values: [], fmt: v => String(v) },
      { id: 'anomaly', label: 'Com Anomalia',      type: 'bool', group: 'dim', fmt: v => String(v) },
    ],
  },
}

const ALL_FIELDS: FieldDef[] = ['core', 'vc', 'lc', 'dim'].flatMap(g =>
  FIELD_GROUPS[g].items
)

const fieldById = (id: string): FieldDef | undefined => ALL_FIELDS.find(f => f.id === id)

// ─── Color system ─────────────────────────────────────────────────────────────

const TONE_COLORS = {
  core: { bg: 'var(--ink-100)',  fg: 'var(--ink-700)',   dot: 'var(--ink-500)' },
  vc:   { bg: '#ede5ff',         fg: 'var(--brand-700)', dot: 'var(--brand-500)' },
  lc:   { bg: '#ffe7df',         fg: '#c2410c',          dot: 'var(--accent-coral)' },
  dim:  { bg: '#e6f2ff',         fg: '#1e40af',          dot: '#3b82f6' },
}

// ─── Data transform ───────────────────────────────────────────────────────────

function buildExplorerCompanies(segmentResult: SegmentProcessResult): ExplorerCompany[] {
  const LC_KEYS = new Set<string>() // no real per-company LC data yet — synthetic only

  return (segmentResult.companies ?? [])
    .filter(c => c.success)
    .map(c => {
      const name = c.companyTitle
      const hasLC = LC_KEYS.has(c.companyKey)
      const eng = c.engagementMetrics

      return {
        id: c.companyKey,
        name,
        plan: c.planType,
        segment: c.subscriptionStatus,
        // core
        roi: c.roi,
        roiBay: c.roiAdjusted ?? null,
        revenue: c.totalRevenue,
        revInfluenced: c.influencedRevenue,
        orders: c.totalOrders,
        influenced: c.influencedOrders,
        pctInfluenced: c.totalOrders > 0 ? (c.influencedOrders / c.totalOrders) * 100 : 0,
        cost: c.proportionalCost,
        ticket: c.totalOrders > 0 ? c.totalRevenue / c.totalOrders : 0,
        conv: c.totalOrders > 0 ? (c.influencedOrders / c.totalOrders) * 100 : 0,
        anomaly: (c.roi ?? 0) > 50 || (c.roi ?? 1) < 0,
        // vc — from engagement metrics or synthetic
        sessionsWithVideo: eng?.withVideo.sessions ?? Math.round(rng(name + 'sv', 200, 8000)),
        sessionsNoVideo: eng?.withoutVideo.sessions ?? Math.round(rng(name + 'snv', 800, 12000)),
        bounceWithVideo: eng?.withVideo.bounceRate != null ? eng.withVideo.bounceRate * 100 : parseFloat(rng(name + 'bv', 22, 58).toFixed(1)),
        avgDurationVideo: eng?.withVideo.avgSessionDuration ?? Math.round(rng(name + 'dv', 30, 180)),
        pagesPerSession: eng?.withVideo.pagesPerSession ?? parseFloat(rng(name + 'pps', 2.0, 6.0).toFixed(1)),
        activeVideos: Math.round(rng(name + 'av', 3, 40)),
        // lc — all synthetic (no real per-company LC pipeline yet)
        hasLC,
        nLives: hasLC ? Math.round(rng(name + 'nl', 2, 8)) : null,
        peakConcurrent: hasLC ? Math.round(rng(name + 'pc', 800, 3500)) : null,
        avgWatchMin: hasLC ? parseFloat(rng(name + 'wm', 8, 22).toFixed(1)) : null,
        convLive: hasLC ? parseFloat(rng(name + 'cl', 4, 15).toFixed(1)) : null,
        revenuePerLive: hasLC ? Math.round(rng(name + 'rpl', 5000, 25000)) : null,
        matchGARate: hasLC ? parseFloat(rng(name + 'mg', 60, 90).toFixed(0)) : null,
        uniqViewersLive: hasLC ? Math.round(rng(name + 'uv', 1200, 5000)) : null,
        reactionsLive: hasLC ? Math.round(rng(name + 'rx', 2000, 12000)) : null,
        chatMsgsLive: hasLC ? Math.round(rng(name + 'cm', 800, 4000)) : null,
      }
    })
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ScopeChip({ tone }: { tone: 'core' | 'vc' | 'lc' | 'dim' }) {
  const c = TONE_COLORS[tone]
  const labels = { core: 'NÚCLEO', vc: 'VC', lc: 'LC', dim: 'DIM' }
  return (
    <span style={{
      fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 4,
      background: c.bg, color: c.fg, letterSpacing: '.05em', textTransform: 'uppercase',
    }}>
      {labels[tone]}
    </span>
  )
}

function MiniKpi({ label, value, accent }: { label: string; value: string | number; accent?: 'success' | 'amber' | 'danger' }) {
  const color = accent === 'success' ? 'var(--success)' : accent === 'amber' ? 'var(--warning)' : accent === 'danger' ? 'var(--danger)' : 'var(--ink-900)'
  return (
    <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 8, padding: 10 }}>
      <div style={{ fontSize: 10, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color, marginTop: 3 }}>{value}</div>
    </div>
  )
}

function AxisSelect({ label, value, onChange, accent, allowNone }: {
  label: string
  value: string | null
  onChange: (v: string | null) => void
  accent: string
  allowNone?: boolean
}) {
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'var(--paper)', border: '1px solid var(--ink-200)',
      borderRadius: 8, padding: '4px 8px 4px 10px', fontSize: 12,
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</span>
      <select
        value={value ?? ''}
        onChange={e => onChange(e.target.value || null)}
        style={{ border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', outline: 'none' }}
      >
        {allowNone && <option value="">— sem cor —</option>}
        {Object.entries(FIELD_GROUPS).map(([gk, g]) => (
          <optgroup key={gk} label={g.label}>
            {g.items.filter(f => f.type === 'num').map(f => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </optgroup>
        ))}
      </select>
    </label>
  )
}

// ─── Scatter Tab ──────────────────────────────────────────────────────────────

function pct(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0
  return sorted[Math.max(0, Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * p / 100)))]
}

function ScatterTab({ xField, yField, colorBy, setX, setY, setColorBy, companies, onSelect, selected }: {
  xField: string
  yField: string
  colorBy: string | null
  setX: (v: string) => void
  setY: (v: string) => void
  setColorBy: (v: string | null) => void
  companies: ExplorerCompany[]
  onSelect: (c: ExplorerCompany | null) => void
  selected: ExplorerCompany | null
}) {
  const fx = fieldById(xField)
  const fy = fieldById(yField)
  const fc = colorBy ? fieldById(colorBy) : null

  const points = companies.filter(c => {
    const vx = (c as unknown as Record<string, unknown>)[xField]
    const vy = (c as unknown as Record<string, unknown>)[yField]
    return vx != null && vy != null
  })

  const xs = points.map(p => (p as unknown as Record<string, unknown>)[xField] as number)
  const ys = points.map(p => (p as unknown as Record<string, unknown>)[yField] as number)

  const meanX = xs.reduce((a, b) => a + b, 0) / (xs.length || 1)
  const meanY = ys.reduce((a, b) => a + b, 0) / (ys.length || 1)
  const num = xs.reduce((a, _, i) => a + (xs[i] - meanX) * (ys[i] - meanY), 0)
  const denX = xs.reduce((a, v) => a + (v - meanX) ** 2, 0)
  const denY = ys.reduce((a, v) => a + (v - meanY) ** 2, 0)
  const den = Math.sqrt(denX * denY)
  const r = den > 0 ? num / den : 0
  const rAbs = Math.abs(r)
  const rTone = rAbs >= 0.7 ? 'success' : rAbs >= 0.4 ? 'amber' : 'gray'
  const rLabel = rAbs >= 0.7 ? 'Forte' : rAbs >= 0.4 ? 'Moderada' : 'Fraca'

  const palette = ['#6d3aff', '#06b6a4', '#f5a524', '#ec4899', '#3b82f6', '#0ea968']
  const groups: Record<string, string> = {}
  if (colorBy) {
    points.forEach(p => {
      const key = String((p as unknown as Record<string, unknown>)[colorBy] ?? '')
      if (!groups[key]) groups[key] = palette[Object.keys(groups).length % palette.length]
    })
  }

  // ── Scale: p5–p95 with 10% padding so outliers don't compress the view ──
  const sortedXs = [...xs].sort((a, b) => a - b)
  const sortedYs = [...ys].sort((a, b) => a - b)
  const medX = sortedXs[Math.floor(sortedXs.length / 2)] ?? meanX
  const medY = sortedYs[Math.floor(sortedYs.length / 2)] ?? meanY

  const xLo = pct(sortedXs, 5),  xHi = pct(sortedXs, 95)
  const yLo = pct(sortedYs, 5),  yHi = pct(sortedYs, 95)
  const xSpan = (xHi - xLo) || Math.max(xHi, 1)
  const ySpan = (yHi - yLo) || Math.max(yHi, 1)
  const xViewMin = xLo - xSpan * 0.10
  const xViewMax = xHi + xSpan * 0.10
  const yViewMin = yLo - ySpan * 0.10
  const yViewMax = yHi + ySpan * 0.10
  const xVR = xViewMax - xViewMin || 1
  const yVR = yViewMax - yViewMin || 1

  const W = 760, H = 400, pad = { l: 64, r: 24, t: 28, b: 52 }
  const plotW = W - pad.l - pad.r
  const plotH = H - pad.t - pad.b

  // Project raw value → pixel, clamped to plot area
  const sx = (v: number) => pad.l + ((Math.max(xViewMin, Math.min(xViewMax, v)) - xViewMin) / xVR) * plotW
  const sy = (v: number) => H - pad.b - ((Math.max(yViewMin, Math.min(yViewMax, v)) - yViewMin) / yVR) * plotH

  // Is a point clipped by the view window?
  const isClipped = (p: ExplorerCompany) => {
    const vx = (p as unknown as Record<string, unknown>)[xField] as number
    const vy = (p as unknown as Record<string, unknown>)[yField] as number
    return vx < xViewMin || vx > xViewMax || vy < yViewMin || vy > yViewMax
  }

  // Regression line clipped to view range
  const slope = denX > 0 ? num / denX : 0
  const intercept = meanY - slope * meanX
  const regY1 = intercept + slope * xViewMin
  const regY2 = intercept + slope * xViewMax

  // Quadrant center positions for labels
  const qMX = sx(medX), qMY = sy(medY)
  const qLabel = {
    tr: { x: (qMX + (pad.l + plotW)) / 2, y: (pad.t + qMY) / 2 },
    tl: { x: (pad.l + qMX) / 2,           y: (pad.t + qMY) / 2 },
    br: { x: (qMX + (pad.l + plotW)) / 2, y: (qMY + (H - pad.b)) / 2 },
    bl: { x: (pad.l + qMX) / 2,           y: (qMY + (H - pad.b)) / 2 },
  }

  // Tick values: 5 evenly spaced across view range
  const xTicks = [0, 0.25, 0.5, 0.75, 1].map(t => xViewMin + t * xVR)
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => yViewMin + t * yVR)

  const [hover, setHover] = useState<{ x: number; y: number; p: ExplorerCompany; color: string } | null>(null)

  const outlierCount = points.filter(p => isClipped(p)).length

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <AxisSelect label="X" value={xField} onChange={v => v && setX(v)} accent="var(--brand-500)" />
        <AxisSelect label="Y" value={yField} onChange={v => v && setY(v)} accent="var(--success)" />
        <AxisSelect label="Cor" value={colorBy} onChange={setColorBy} accent="var(--accent-coral)" allowNone />
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => { setX(yField); setY(xField) }}
          style={{ display: 'flex', alignItems: 'center', gap: 4 }}
        >
          ⇄ Inverter
        </button>
        <span style={{ flex: 1 }} />
        {outlierCount > 0 && (
          <span style={{ fontSize: 11, color: 'var(--warning)', background: 'var(--warning-bg)', padding: '4px 10px', borderRadius: 999, fontWeight: 600 }}>
            {outlierCount} outlier{outlierCount > 1 ? 's' : ''} fora da escala
          </span>
        )}
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 10px',
          background: rTone === 'success' ? 'var(--success-bg)' : rTone === 'amber' ? 'var(--warning-bg)' : 'var(--ink-100)',
          color: rTone === 'success' ? 'var(--success)' : rTone === 'amber' ? 'var(--warning)' : 'var(--ink-600)',
          borderRadius: 999, fontSize: 11, fontWeight: 700,
        }}>
          r = {r.toFixed(2)} · {rLabel}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 300px' : '1fr', gap: 16 }}>
        <div style={{ position: 'relative', background: 'var(--ink-50)', borderRadius: 10, padding: 8 }}>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
            {/* Plot area clip */}
            <defs>
              <clipPath id="plotClip">
                <rect x={pad.l} y={pad.t} width={plotW} height={plotH} />
              </clipPath>
            </defs>

            {/* Quadrant fills — subtle tint */}
            <rect x={qMX} y={pad.t} width={pad.l + plotW - qMX} height={qMY - pad.t} fill="rgba(109,58,255,0.03)" />
            <rect x={pad.l} y={pad.t} width={qMX - pad.l} height={qMY - pad.t} fill="rgba(0,0,0,0.01)" />
            <rect x={qMX} y={qMY} width={pad.l + plotW - qMX} height={H - pad.b - qMY} fill="rgba(0,0,0,0.01)" />
            <rect x={pad.l} y={qMY} width={qMX - pad.l} height={H - pad.b - qMY} fill="rgba(220,38,38,0.02)" />

            {/* Quadrant divider lines */}
            <line x1={qMX} x2={qMX} y1={pad.t} y2={H - pad.b} stroke="var(--ink-300)" strokeDasharray="4 3" strokeWidth={1} />
            <line x1={pad.l} x2={pad.l + plotW} y1={qMY} y2={qMY} stroke="var(--ink-300)" strokeDasharray="4 3" strokeWidth={1} />

            {/* Quadrant labels — centered in each quadrant */}
            <text x={qLabel.tr.x} y={qLabel.tr.y} fontSize="9" fill="rgba(109,58,255,0.5)" textAnchor="middle" fontWeight="700">↗ ALTO · ALTO</text>
            <text x={qLabel.tl.x} y={qLabel.tl.y} fontSize="9" fill="var(--ink-400)" textAnchor="middle" fontWeight="700">↖ BAIXO · ALTO</text>
            <text x={qLabel.br.x} y={qLabel.br.y} fontSize="9" fill="var(--ink-400)" textAnchor="middle" fontWeight="700">↘ ALTO · BAIXO</text>
            <text x={qLabel.bl.x} y={qLabel.bl.y} fontSize="9" fill="rgba(220,38,38,0.4)" textAnchor="middle" fontWeight="700">↙ BAIXO · BAIXO</text>

            {/* Regression line (clipped) */}
            <line
              x1={sx(xViewMin)} y1={sy(regY1)}
              x2={sx(xViewMax)} y2={sy(regY2)}
              stroke="var(--brand-300)" strokeWidth={1.5} strokeDasharray="6 4"
              clipPath="url(#plotClip)"
            />

            {/* Axes */}
            <line x1={pad.l} x2={pad.l + plotW} y1={H - pad.b} y2={H - pad.b} stroke="var(--ink-300)" strokeWidth={1} />
            <line x1={pad.l} x2={pad.l} y1={pad.t} y2={H - pad.b} stroke="var(--ink-300)" strokeWidth={1} />

            {/* Axis labels */}
            <text x={pad.l + plotW / 2} y={H - 8} fontSize="11" fill="var(--ink-700)" textAnchor="middle" fontWeight="600">{fx?.label}</text>
            <text x={14} y={pad.t + plotH / 2} fontSize="11" fill="var(--ink-700)" textAnchor="middle" fontWeight="600" transform={`rotate(-90 14 ${pad.t + plotH / 2})`}>{fy?.label}</text>

            {/* X tick labels */}
            {xTicks.map((v, i) => (
              <text key={i} x={sx(v)} y={H - pad.b + 14} fontSize="8" fill="var(--ink-400)" textAnchor="middle">{fx?.fmt(v)}</text>
            ))}
            {/* Y tick labels */}
            {yTicks.map((v, i) => (
              <text key={i} x={pad.l - 5} y={sy(v) + 3} fontSize="8" fill="var(--ink-400)" textAnchor="end">{fy?.fmt(v)}</text>
            ))}

            {/* Points */}
            {points.map((p, i) => {
              const color = colorBy ? groups[String((p as unknown as Record<string, unknown>)[colorBy] ?? '')] ?? '#6d3aff' : '#6d3aff'
              const sel = selected?.id === p.id
              const rawX = (p as unknown as Record<string, unknown>)[xField] as number
              const rawY = (p as unknown as Record<string, unknown>)[yField] as number
              const clipped = isClipped(p)
              const px = sx(rawX)
              const py = sy(rawY)
              const r = sel ? 8 : 6

              if (clipped) {
                // Outlier: diamond marker at clamped position with dashed ring
                return (
                  <g key={i} style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setHover({ x: px, y: py, p, color })}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => onSelect(sel ? null : p)}
                    clipPath="url(#plotClip)"
                  >
                    <polygon
                      points={`${px},${py - 8} ${px + 7},${py} ${px},${py + 8} ${px - 7},${py}`}
                      fill={color} fillOpacity={0.5}
                      stroke={color} strokeWidth={1.5} strokeDasharray="2 2"
                    />
                  </g>
                )
              }

              return (
                <g key={i} style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHover({ x: px, y: py, p, color })}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => onSelect(sel ? null : p)}
                  clipPath="url(#plotClip)"
                >
                  <circle cx={px} cy={py} r={r + 4} fill={color} fillOpacity={0} />
                  <circle cx={px} cy={py} r={r} fill={color} fillOpacity={sel ? 1 : 0.75} stroke="var(--paper)" strokeWidth={sel ? 2.5 : 1.5} />
                  {sel && (
                    <text x={px} y={py - 13} fontSize="10" fontWeight="700" fill={color} textAnchor="middle"
                      style={{ filter: 'drop-shadow(0 0 3px var(--paper))' }}
                    >{p.name}</text>
                  )}
                </g>
              )
            })}

            {/* Hover tooltip */}
            {hover && (() => {
              const rawX = (hover.p as unknown as Record<string, unknown>)[xField] as number
              const rawY = (hover.p as unknown as Record<string, unknown>)[yField] as number
              const clipped = isClipped(hover.p)
              const lines = [
                hover.p.name,
                `${fx?.label}: ${fx?.fmt(rawX)}`,
                `${fy?.label}: ${fy?.fmt(rawY)}`,
                ...(clipped ? ['⚠ outlier fora da escala'] : []),
              ]
              const tw = 200, th = 16 + lines.length * 16
              const tx = Math.min(hover.x + 12, W - tw - 4)
              const ty = Math.max(hover.y - th - 4, 4)
              return (
                <g style={{ pointerEvents: 'none' }}>
                  <rect x={tx} y={ty} width={tw} height={th} rx={6} fill="var(--ink-900)" opacity={0.95} />
                  {lines.map((line, i) => (
                    <text key={i} x={tx + 10} y={ty + 14 + i * 16}
                      fontSize={i === 0 ? 11 : 10}
                      fontWeight={i === 0 ? 700 : 400}
                      fill={i === lines.length - 1 && clipped ? '#fbbf24' : i === 0 ? 'white' : 'rgba(255,255,255,0.75)'}
                    >{line}</text>
                  ))}
                </g>
              )
            })()}
          </svg>

          {/* Legend */}
          {fc && Object.keys(groups).length > 0 && (
            <div style={{ display: 'flex', gap: 12, padding: '8px 12px', borderTop: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
              {Object.entries(groups).map(([k, c]) => (
                <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-700)' }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
                  {k} <span style={{ color: 'var(--ink-400)' }}>({points.filter(p => String((p as unknown as Record<string, unknown>)[colorBy!] ?? '') === k).length})</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {selected && (
          <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 10, padding: 14, position: 'relative', overflowY: 'auto', maxHeight: 440 }}>
            <button
              onClick={() => onSelect(null)}
              style={{ position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-400)', fontSize: 16, lineHeight: 1 }}
            >×</button>
            <div style={{ fontSize: 11, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Empresa</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>{selected.name}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 2 }}>{selected.plan} · {selected.segment}</div>
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ALL_FIELDS.filter(f => f.type === 'num' && (selected as unknown as Record<string, unknown>)[f.id] != null).slice(0, 16).map(f => (
                <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--ink-100)' }}>
                  <span style={{ color: 'var(--ink-600)' }}>{f.label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--ink-900)', fontFamily: 'var(--font-mono)' }}>
                    {f.fmt((selected as unknown as Record<string, unknown>)[f.id] as number)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginTop: 14 }}>
        <MiniKpi label="Empresas plotadas" value={points.length} />
        <MiniKpi label="Correlação r" value={r.toFixed(3)} accent={rTone === 'success' ? 'success' : rTone === 'amber' ? 'amber' : undefined} />
        <MiniKpi label={`Mediana ${fx?.label}`} value={fx?.fmt(medX) ?? '—'} />
        <MiniKpi label={`Mediana ${fy?.label}`} value={fy?.fmt(medY) ?? '—'} />
      </div>
    </div>
  )
}

// ─── Compare Tab ──────────────────────────────────────────────────────────────

function CompareTab({ companies, showLC }: { companies: ExplorerCompany[]; showLC: boolean }) {
  const [splitBy, setSplitBy] = useState<string>('roi_q')
  const [chartMetrics, setChartMetrics] = useState<string[]>(['roi', 'conv', 'pctInfluenced'])

  const groups = useMemo(() => {
    type Group = { label: string; items: ExplorerCompany[]; color: string }
    const sorted = (key: string) =>
      [...companies].sort((a, b) => ((b as unknown as Record<string, unknown>)[key] as number || 0) - ((a as unknown as Record<string, unknown>)[key] as number || 0))

    if (['roi_q', 'rev_q', 'conv_q', 'lives_q'].includes(splitBy)) {
      const keyMap: Record<string, string> = { roi_q: 'roi', rev_q: 'revenue', conv_q: 'conv', lives_q: 'nLives' }
      const key = keyMap[splitBy]
      const arr = sorted(key).filter(c => (c as unknown as Record<string, unknown>)[key] != null)
      const q = Math.ceil(arr.length / 4)
      return [
        { label: 'Top 25%',    items: arr.slice(0, q),      color: 'var(--success)' },
        { label: 'Q2',         items: arr.slice(q, q * 2),  color: 'var(--accent-blue, #3b82f6)' },
        { label: 'Q3',         items: arr.slice(q * 2, q * 3), color: 'var(--warning)' },
        { label: 'Bottom 25%', items: arr.slice(q * 3),     color: 'var(--danger)' },
      ] as Group[]
    }
    if (splitBy === 'plan') {
      const map: Record<string, ExplorerCompany[]> = {}
      companies.forEach(c => { (map[c.plan] = map[c.plan] || []).push(c) })
      const colors: Record<string, string> = { Basic: 'var(--ink-500)', Pro: 'var(--brand-500)', Enterprise: 'var(--accent-coral)' }
      return Object.entries(map).map(([k, v]) => ({ label: k, items: v, color: colors[k] || 'var(--ink-500)' })) as Group[]
    }
    return [] as Group[]
  }, [splitBy, companies])

  const avg = (items: ExplorerCompany[], k: string): number | null => {
    const vals = items.map(c => (c as unknown as Record<string, unknown>)[k] as number | null).filter(v => v != null) as number[]
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null
  }

  const rowsByCat = [
    { cat: 'Resultado', tone: 'core' as const, metrics: ['roi', 'revenue', 'cost', 'orders', 'influenced', 'conv', 'pctInfluenced', 'ticket'] },
    { cat: 'Video Commerce', tone: 'vc' as const, metrics: ['sessionsWithVideo', 'bounceWithVideo', 'avgDurationVideo', 'pagesPerSession', 'activeVideos'] },
    ...(showLC ? [{ cat: 'Live Commerce', tone: 'lc' as const, metrics: ['nLives', 'peakConcurrent', 'avgWatchMin', 'convLive', 'revenuePerLive', 'matchGARate'] }] : []),
  ]

  const rowDelta = (mid: string) => {
    const vals = groups.map(g => avg(g.items, mid)).filter(v => v != null) as number[]
    if (vals.length < 2) return 0
    const max = Math.max(...vals), min = Math.min(...vals)
    return min ? (max - min) / Math.abs(min) : 0
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-700)' }}>Dividir por:</span>
        <div style={{ display: 'flex', gap: 4, background: 'var(--ink-100)', borderRadius: 999, padding: 3 }}>
          {[
            { k: 'roi_q', l: 'ROI' },
            { k: 'rev_q', l: 'Receita' },
            { k: 'conv_q', l: 'Conversão' },
            ...(showLC ? [{ k: 'lives_q', l: 'Nº Lives' }] : []),
            { k: 'plan', l: 'Plano' },
          ].map(o => (
            <button key={o.k} onClick={() => setSplitBy(o.k)} style={{
              padding: '5px 12px', borderRadius: 999, border: 'none',
              background: splitBy === o.k ? 'var(--paper)' : 'transparent',
              fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
              color: splitBy === o.k ? 'var(--brand-700)' : 'var(--ink-600)',
              boxShadow: splitBy === o.k ? 'var(--shadow-sm)' : 'none',
            }}>{o.l}</button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        {groups.map((g, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-700)' }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: g.color }} />
            {g.label} <span style={{ color: 'var(--ink-400)' }}>({g.items.length})</span>
          </span>
        ))}
      </div>

      {/* Chart */}
      <div style={{ background: 'var(--ink-50)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {chartMetrics.map((m, i) => (
            <select key={i} value={m}
              onChange={e => setChartMetrics(cm => { const nx = [...cm]; nx[i] = e.target.value; return nx })}
              style={{ padding: '4px 8px', border: '1px solid var(--ink-200)', borderRadius: 6, background: 'var(--paper)', fontSize: 11, fontFamily: 'inherit', fontWeight: 600 }}
            >
              {ALL_FIELDS.filter(f => f.type === 'num').map(f => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
          {chartMetrics.map(mid => {
            const f = fieldById(mid)
            if (!f) return null
            const vals = groups.map(g => ({ g, v: avg(g.items, mid) ?? 0 }))
            const max = Math.max(...vals.map(v => v.v)) || 1
            return (
              <div key={mid}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-700)', marginBottom: 8 }}>{f.label}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {vals.map((v, i) => (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--ink-600)', marginBottom: 2 }}>
                        <span>{v.g.label}</span>
                        <span style={{ fontWeight: 700, color: 'var(--ink-900)' }}>{f.fmt(v.v)}</span>
                      </div>
                      <div style={{ height: 8, background: 'var(--ink-100)', borderRadius: 4 }}>
                        <div style={{ height: '100%', width: `${(v.v / max) * 100}%`, background: v.g.color, borderRadius: 4 }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', border: '1px solid var(--ink-200)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--ink-50)' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: 'var(--ink-600)', fontWeight: 700, position: 'sticky', left: 0, background: 'var(--ink-50)' }}>Métrica</th>
              {groups.map((g, i) => (
                <th key={i} style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, color: g.color, fontWeight: 700 }}>{g.label}</th>
              ))}
              <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, color: 'var(--ink-600)', fontWeight: 700 }}>Δ</th>
            </tr>
          </thead>
          <tbody>
            {rowsByCat.map(rc => (
              <>
                <tr key={rc.cat + '_header'}>
                  <td colSpan={groups.length + 2} style={{
                    padding: '8px 14px',
                    background: TONE_COLORS[rc.tone].bg,
                    fontSize: 10, fontWeight: 700,
                    color: TONE_COLORS[rc.tone].fg,
                    textTransform: 'uppercase', letterSpacing: '.06em',
                  }}>{rc.cat}</td>
                </tr>
                {rc.metrics.map(mid => {
                  const f = fieldById(mid)
                  if (!f) return null
                  const delta = rowDelta(mid)
                  const isHighlight = delta > 0.5
                  const first = groups[0] ? avg(groups[0].items, mid) : null
                  const last = groups[groups.length - 1] ? avg(groups[groups.length - 1].items, mid) : null
                  const dPct = first != null && last != null && last !== 0 ? ((first - last) / Math.abs(last)) * 100 : 0
                  return (
                    <tr key={mid} style={{ background: isHighlight ? 'rgba(245,165,36,0.06)' : 'transparent', borderTop: '1px solid var(--ink-100)' }}>
                      <td style={{ padding: '8px 14px', fontWeight: 500, color: 'var(--ink-800)', position: 'sticky', left: 0, background: isHighlight ? '#fefcf6' : 'var(--paper)' }}>
                        {f.label}
                      </td>
                      {groups.map((g, i) => {
                        const v = avg(g.items, mid)
                        return (
                          <td key={i} style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--ink-800)' }}>
                            {v != null ? f.fmt(v) : '—'}
                          </td>
                        )
                      })}
                      <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: dPct >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                        {dPct >= 0 ? '+' : ''}{dPct.toFixed(0)}%
                      </td>
                    </tr>
                  )
                })}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Filter Draft ─────────────────────────────────────────────────────────────

type DraftState =
  | { stage: 'field' }
  | { stage: 'op'; field: FieldDef }
  | { stage: 'value'; field: FieldDef; op: string }

function FilterDraft({ draft, onPickField, onPickOp, onCommit, onCancel, showLC }: {
  draft: DraftState
  onPickField: (f: FieldDef) => void
  onPickOp: (op: string) => void
  onCommit: (value: number | string | boolean) => void
  onCancel: () => void
  showLC: boolean
}) {
  const [val, setVal] = useState('')

  if (draft.stage === 'field') {
    return (
      <div style={{ position: 'relative' }}>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 2, padding: 8,
          background: 'var(--paper)', border: '1px solid var(--ink-200)',
          borderRadius: 10, boxShadow: 'var(--shadow-lg)', maxHeight: 280, overflowY: 'auto', minWidth: 240,
        }}>
          {Object.entries(FIELD_GROUPS).map(([gk, g]) => {
            if (gk === 'lc' && !showLC) return null
            return (
              <div key={gk}>
                <div style={{ fontSize: 9, padding: '6px 8px 2px', color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>{g.label}</div>
                {g.items.map(f => (
                  <div
                    key={f.id}
                    onClick={() => onPickField(f)}
                    style={{ padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--ink-100)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    {f.label}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (draft.stage === 'op') {
    const ops = draft.field.type === 'num' ? ['>', '>=', '<', '<=', '=', '!='] : ['=', '!=', '~']
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: 'var(--paper)', border: '1px solid var(--brand-300)', borderRadius: 999 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>{draft.field.label}</span>
        {ops.map(op => (
          <button key={op} onClick={() => onPickOp(op)} style={{ padding: '2px 8px', border: '1px solid var(--ink-200)', background: 'var(--paper)', borderRadius: 4, fontFamily: 'var(--font-mono)', fontSize: 11, cursor: 'pointer' }}>
            {op}
          </button>
        ))}
        <button onClick={onCancel} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-400)', fontSize: 16, lineHeight: 1 }}>×</button>
      </span>
    )
  }

  if (draft.stage === 'value') {
    const isStr = draft.field.type === 'str' || (draft.field.values && draft.field.values.length > 0)
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 4px 4px 10px', background: 'var(--paper)', border: '1px solid var(--brand-500)', borderRadius: 999 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>{draft.field.label}</span>
        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-500)' }}>{draft.op}</span>
        {isStr ? (
          <select autoFocus value={val} onChange={e => setVal(e.target.value)}
            style={{ padding: '3px 6px', border: '1px solid var(--ink-200)', borderRadius: 4, fontSize: 11 }}
          >
            <option value="">—</option>
            {(draft.field.values || []).map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        ) : (
          <input
            autoFocus type="number" value={val}
            onChange={e => setVal(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && val && onCommit(parseFloat(val))}
            placeholder="valor"
            style={{ width: 70, padding: '3px 6px', border: '1px solid var(--ink-200)', borderRadius: 4, fontSize: 11, fontFamily: 'var(--font-mono)' }}
          />
        )}
        <button
          onClick={() => val && onCommit(draft.field.type === 'num' ? parseFloat(val) : val)}
          style={{ background: 'var(--brand-500)', color: 'white', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}
        >
          OK
        </button>
      </span>
    )
  }

  return null
}

// ─── Segment Tab ──────────────────────────────────────────────────────────────

function SegmentTab({ filters, setFilters, companies, allCount, showLC }: {
  filters: FilterDef[]
  setFilters: (fn: (prev: FilterDef[]) => FilterDef[]) => void
  companies: ExplorerCompany[]
  allCount: number
  showLC: boolean
}) {
  const [draft, setDraft] = useState<DraftState | null>(null)
  const [groupBy, setGroupBy] = useState<'plan' | 'segment'>('plan')

  const addFilter = () => setDraft({ stage: 'field' })
  const pickField = (f: FieldDef) => setDraft({ stage: 'op', field: f })
  const pickOp = (op: string) => setDraft(d => d && d.stage === 'op' ? { stage: 'value', field: d.field, op } : d)
  const commit = (value: number | string | boolean) => {
    if (draft && draft.stage === 'value') {
      setFilters(fs => [...fs, { field: draft.field.id, op: draft.op, value }])
      setDraft(null)
    }
  }
  const removeFilter = (i: number) => setFilters(fs => fs.filter((_, k) => k !== i))

  const totalRev = companies.reduce((a, c) => a + c.revenue, 0)
  const totalRevInf = companies.reduce((a, c) => a + c.revInfluenced, 0)
  const avgRoi = (() => {
    const vals = companies.map(c => c.roi).filter(v => v != null) as number[]
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0
  })()
  const totalLives = companies.reduce((a, c) => a + (c.nLives ?? 0), 0)

  const histo = (() => {
    const map: Record<string, number> = {}
    companies.forEach(c => {
      const key = String(c[groupBy])
      map[key] = (map[key] ?? 0) + 1
    })
    return Object.entries(map).sort((a, b) => b[1] - a[1])
  })()
  const histoMax = Math.max(1, ...histo.map(([, n]) => n))

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: 12, background: 'var(--ink-50)', borderRadius: 10, marginBottom: 14 }}>
        {filters.length === 0 && !draft && (
          <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>Sem filtros · mostrando todas as {allCount} empresas</span>
        )}
        {filters.map((f, i) => {
          const fld = fieldById(f.field)
          if (!fld) return null
          return (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 4px 4px 10px', background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
              <ScopeChip tone={fld.group} />
              <span>{fld.label}</span>
              <span style={{ color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>{f.op}</span>
              <span style={{ color: 'var(--brand-700)', fontFamily: 'var(--font-mono)' }}>{String(f.value)}</span>
              <button onClick={() => removeFilter(i)} style={{ background: 'var(--ink-100)', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ×
              </button>
            </span>
          )
        })}
        {draft && (
          <FilterDraft
            draft={draft}
            onPickField={pickField}
            onPickOp={pickOp}
            onCommit={commit}
            onCancel={() => setDraft(null)}
            showLC={showLC}
          />
        )}
        {!draft && (
          <button onClick={addFilter} className="btn btn-ghost btn-sm" style={{ borderStyle: 'dashed', display: 'flex', alignItems: 'center', gap: 4 }}>
            + Adicionar filtro
          </button>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand-700)', background: 'var(--brand-50)', padding: '5px 12px', borderRadius: 999 }}>
          {companies.length} de {allCount} · {Math.round(companies.length / Math.max(allCount, 1) * 100)}%
        </span>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${showLC ? 5 : 4},1fr)`, gap: 10, marginBottom: 14 }}>
        <MiniKpi label="Empresas" value={companies.length} />
        <MiniKpi label="ROI Médio" value={`${formatRoiValue(avgRoi)}×`} />
        <MiniKpi label="Receita Total" value={fmtBRL(totalRev)} />
        <MiniKpi label="Rec. Influenciada" value={fmtBRL(totalRevInf)} />
        {showLC && <MiniKpi label="Total Lives" value={totalLives} />}
      </div>

      {/* Histogram */}
      <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Distribuição</div>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value as 'plan' | 'segment')}
            style={{ padding: '4px 8px', border: '1px solid var(--ink-200)', borderRadius: 6, fontSize: 11, fontFamily: 'inherit' }}
          >
            <option value="plan">por Plano</option>
            <option value="segment">por Segmento</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', height: 100 }}>
          {histo.map(([k, n]) => (
            <div key={k} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-700)' }}>{n}</div>
              <div style={{ width: '100%', height: `${(n / histoMax) * 70}px`, background: 'linear-gradient(180deg, var(--brand-400), var(--brand-600))', borderRadius: '6px 6px 0 0' }} />
              <div style={{ fontSize: 10, color: 'var(--ink-500)', fontWeight: 600 }}>{k}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Companies table */}
      <div style={{ overflowX: 'auto', border: '1px solid var(--ink-200)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--ink-50)' }}>
              {['Empresa', 'Plano', 'Pedidos', 'Receita', 'ROI', 'Custo', 'Conv%'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Empresa' || h === 'Plano' ? 'left' : 'right', fontSize: 11, color: 'var(--ink-600)', fontWeight: 700 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {companies.map(c => (
              <tr key={c.id} style={{ borderTop: '1px solid var(--ink-100)' }}>
                <td style={{ padding: '8px 14px', fontWeight: 600 }}>{c.name}</td>
                <td style={{ padding: '8px 14px' }}>{c.plan}</td>
                <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtNum(c.orders)}</td>
                <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtBRL(c.revenue)}</td>
                <td style={{
                  padding: '8px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700,
                  color: (c.roi ?? 0) >= 5 ? 'var(--success)' : (c.roi ?? 0) >= 2 ? 'var(--warning)' : 'var(--danger)',
                }}>
                  {c.roi != null ? `${formatRoiValue(c.roi)}×` : '—'}
                </td>
                <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{fmtBRL(c.cost)}</td>
                <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{c.conv.toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Rank Tab ─────────────────────────────────────────────────────────────────

function MiniSparkline({ values }: { values: number[] }) {
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const W = 60, H = 20
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W
    const y = H - ((v - min) / range) * H
    return `${x},${y}`
  }).join(' ')
  return (
    <svg width={W} height={H} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke="var(--brand-500)" strokeWidth={1.5} />
    </svg>
  )
}

function RankTab({ companies }: { companies: ExplorerCompany[]; showLC: boolean }) {
  const [sortBy, setSortBy] = useState<string>('roi')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [secondary] = useState<string[]>(['revenue', 'orders', 'conv', 'pctInfluenced'])
  const [q, setQ] = useState('')

  const sorted = [...companies]
    .filter(c => c.name.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => {
      const va = ((a as unknown as Record<string, unknown>)[sortBy] as number) || 0
      const vb = ((b as unknown as Record<string, unknown>)[sortBy] as number) || 0
      return sortDir === 'desc' ? vb - va : va - vb
    })

  const fSort = fieldById(sortBy)

  // Deterministic sparkline per company
  const spark = (id: string): number[] =>
    Array.from({ length: 6 }, (_, i) => 50 + Math.sin(seed(id) * 0.001 + i * 1.3) * 25 + i * 4)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-700)' }}>Ordenar por</span>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          style={{ padding: '6px 10px', border: '1px solid var(--ink-200)', borderRadius: 8, fontSize: 12, fontFamily: 'inherit', fontWeight: 600 }}
        >
          {ALL_FIELDS.filter(f => f.type === 'num').map(f => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        <button onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')} className="btn btn-ghost btn-sm">
          {sortDir === 'desc' ? '↓ desc' : '↑ asc'}
        </button>
        <span style={{ flex: 1 }} />
        <input
          value={q} onChange={e => setQ(e.target.value)}
          placeholder="Buscar empresa…"
          className="input"
          style={{ width: 200, height: 32, fontSize: 12 }}
        />
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => {
            const header = ['#', 'Empresa', 'Plano', fSort?.label ?? sortBy, ...secondary.map(s => fieldById(s)?.label ?? s)].join(',')
            const rows = sorted.map((c, i) => [
              i + 1, c.name, c.plan,
              fSort?.fmt((c as unknown as Record<string, unknown>)[sortBy] as number) ?? '',
              ...secondary.map(s => {
                const v = (c as unknown as Record<string, unknown>)[s]
                return v != null ? fieldById(s)?.fmt(v as number) ?? '' : ''
              })
            ].join(','))
            const csv = [header, ...rows].join('\n')
            const blob = new Blob([csv], { type: 'text/csv' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url; a.download = 'explorador-ranking.csv'; a.click()
            URL.revokeObjectURL(url)
          }}
        >
          ↓ CSV
        </button>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--ink-200)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--ink-50)' }}>
              <th style={{ padding: '10px 14px', textAlign: 'left', width: 50, color: 'var(--ink-600)', fontSize: 11, fontWeight: 700 }}>#</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--ink-600)', fontSize: 11, fontWeight: 700 }}>Empresa</th>
              <th style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--brand-700)', fontSize: 11, fontWeight: 700 }}>{fSort?.label} ↓</th>
              {secondary.map(s => {
                const fs = fieldById(s)
                return <th key={s} style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--ink-600)', fontSize: 11, fontWeight: 700 }}>{fs?.label}</th>
              })}
              <th style={{ padding: '10px 14px', textAlign: 'center', color: 'var(--ink-600)', fontSize: 11, fontWeight: 700 }}>Tendência</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c, i) => (
              <tr key={c.id} style={{ borderTop: '1px solid var(--ink-100)' }}>
                <td style={{ padding: '10px 14px', color: 'var(--ink-500)', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>#{i + 1}</td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ fontWeight: 700 }}>{c.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--ink-500)' }}>{c.plan} · {c.segment}</div>
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14, color: 'var(--brand-700)' }}>
                  {fSort?.fmt(((c as unknown as Record<string, unknown>)[sortBy] as number) ?? 0) ?? '—'}
                </td>
                {secondary.map(s => {
                  const fs = fieldById(s)
                  const v = (c as unknown as Record<string, unknown>)[s]
                  return (
                    <td key={s} style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: v == null ? 'var(--ink-300)' : 'var(--ink-700)' }}>
                      {v != null && fs ? fs.fmt(v as number) : '—'}
                    </td>
                  )
                })}
                <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                  <MiniSparkline values={spark(c.id)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Left Field Panel ─────────────────────────────────────────────────────────

function FieldPanel({ xField, yField, colorBy, activeSlot, setActiveSlot, onFieldClick, showLC, showVC }: {
  xField: string
  yField: string
  colorBy: string | null
  activeSlot: Slot
  setActiveSlot: (s: Slot) => void
  onFieldClick: (id: string) => void
  showLC: boolean
  showVC: boolean
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  return (
    <div style={{ borderRight: '1px solid var(--ink-200)', background: 'var(--paper)', overflowY: 'auto', maxHeight: 'calc(100vh - 240px)' }}>
      {/* Slot selector */}
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--ink-100)', position: 'sticky', top: 0, background: 'var(--paper)', zIndex: 2 }}>
        <div style={{ fontSize: 11, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700, marginBottom: 8 }}>Campos disponíveis</div>
        <div style={{ display: 'flex', gap: 4, background: 'var(--ink-100)', borderRadius: 999, padding: 3 }}>
          {([
            { k: 'x' as Slot, label: 'Eixo X', color: 'var(--brand-500)' },
            { k: 'y' as Slot, label: 'Eixo Y', color: 'var(--success)' },
            { k: 'color' as Slot, label: 'Cor', color: 'var(--accent-coral)' },
          ]).map(s => (
            <button key={s.k} onClick={() => setActiveSlot(s.k)} style={{
              flex: 1, padding: '5px 8px', borderRadius: 999, border: 'none',
              background: activeSlot === s.k ? 'var(--paper)' : 'transparent',
              fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
              color: activeSlot === s.k ? s.color : 'var(--ink-600)',
              boxShadow: activeSlot === s.k ? 'var(--shadow-sm)' : 'none',
            }}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Field groups */}
      {Object.entries(FIELD_GROUPS).map(([gk, g]) => {
        if (gk === 'lc' && !showLC) return null
        if (gk === 'vc' && !showVC) return null
        const isCollapsed = collapsed[gk]
        return (
          <div key={gk} style={{ borderBottom: '1px solid var(--ink-100)' }}>
            <div
              onClick={() => setCollapsed(c => ({ ...c, [gk]: !c[gk] }))}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', cursor: 'pointer', userSelect: 'none' }}
            >
              <span style={{ fontSize: 10, color: 'var(--ink-400)' }}>{isCollapsed ? '›' : '⌄'}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-700)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{g.label}</span>
              <ScopeChip tone={g.tone} />
              <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--ink-400)' }}>{g.items.length}</span>
            </div>
            {!isCollapsed && (
              <div style={{ padding: '2px 8px 8px' }}>
                {g.items.map(f => {
                  const isX = f.id === xField, isY = f.id === yField, isC = f.id === colorBy
                  const sel = isX || isY || isC
                  return (
                    <div key={f.id} onClick={() => onFieldClick(f.id)} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                      borderRadius: 6, cursor: 'pointer', fontSize: 12,
                      background: sel ? 'var(--brand-50)' : 'transparent',
                      color: sel ? 'var(--brand-700)' : 'var(--ink-700)',
                      fontWeight: sel ? 600 : 500,
                    }}>
                      <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.label}</span>
                      {isX && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--brand-500)', background: 'var(--paper)', padding: '1px 4px', borderRadius: 3 }}>X</span>}
                      {isY && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--success)', background: 'var(--paper)', padding: '1px 4px', borderRadius: 3 }}>Y</span>}
                      {isC && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--accent-coral)', background: 'var(--paper)', padding: '1px 4px', borderRadius: 3 }}>COR</span>}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Main ExplorerPage ────────────────────────────────────────────────────────

export interface ExplorerPageProps {
  segmentResult: SegmentProcessResult | null
  productMode: 'stories' | 'live'
}

export function ExplorerPage({ segmentResult, productMode }: ExplorerPageProps) {
  const [tab, setTab] = useState<'scatter' | 'compare' | 'segment' | 'rank'>('scatter')
  const [activeSlot, setActiveSlot] = useState<Slot>('x')
  const [xField, setXField] = useState('roi')
  const [yField, setYField] = useState('revenue')
  const [colorBy, setColorBy] = useState<string | null>(null)
  const [filters, setFilters] = useState<FilterDef[]>([])
  const [selectedCo, setSelectedCo] = useState<ExplorerCompany | null>(null)

  const showLC = productMode === 'live'
  const showVC = true

  const allCompanies = useMemo(() => {
    if (!segmentResult) return []
    return buildExplorerCompanies(segmentResult)
  }, [segmentResult])

  const filteredCompanies = useMemo(() => {
    return allCompanies.filter(c =>
      filters.every(f => {
        const v = (c as unknown as Record<string, unknown>)[f.field]
        if (v == null) return false
        if (f.op === '>')  return (v as number) >  (f.value as number)
        if (f.op === '>=') return (v as number) >= (f.value as number)
        if (f.op === '<')  return (v as number) <  (f.value as number)
        if (f.op === '<=') return (v as number) <= (f.value as number)
        if (f.op === '=')  return String(v) === String(f.value)
        if (f.op === '!=') return String(v) !== String(f.value)
        if (f.op === '~')  return String(v).toLowerCase().includes(String(f.value).toLowerCase())
        return true
      })
    )
  }, [allCompanies, filters])

  const handleFieldClick = (fid: string) => {
    if (activeSlot === 'x') setXField(fid)
    else if (activeSlot === 'y') setYField(fid)
    else if (activeSlot === 'color') setColorBy(fid)
  }

  if (!segmentResult || allCompanies.length === 0) {
    const isLiveMode = productMode === 'live'
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>⟁</div>
        {isLiveMode ? (
          <>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink-800)', marginBottom: 8 }}>
              Explorador indisponível no modo Live Commerce
            </div>
            <div style={{ fontSize: 14, color: 'var(--ink-500)', maxWidth: 440, lineHeight: 1.6 }}>
              O Explorador analisa padrões <strong>entre múltiplas empresas</strong>. Live Commerce só suporta
              processamento por empresa individual — análise comparativa não está disponível nesse modo.
            </div>
            <div style={{ marginTop: 20, padding: '14px 20px', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', borderRadius: 10, fontSize: 13, color: 'var(--ink-600)', maxWidth: 380, textAlign: 'left' }}>
              <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--ink-800)' }}>Para usar o Explorador:</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div>1. Mude para <strong>Video Commerce</strong> no filtro acima</div>
                <div>2. Processe um <strong>segmento completo</strong> no Dashboard</div>
                <div>3. Volte ao Explorador para análise cruzada</div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink-800)', marginBottom: 8 }}>
              Nenhum dado carregado
            </div>
            <div style={{ fontSize: 14, color: 'var(--ink-500)', maxWidth: 400, lineHeight: 1.6 }}>
              Processe um <strong>segmento completo</strong> no Dashboard para explorar padrões entre empresas aqui.
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="fadeIn">
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>Lab</div>
          <h1 style={{ margin: '4px 0 4px', fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>Explorador de Dados</h1>
          <div style={{ fontSize: 13, color: 'var(--ink-500)' }}>
            Análise exploratória de métricas entre empresas ·{' '}
            <strong>{filteredCompanies.length}</strong> de <strong>{allCompanies.length}</strong> empresas no escopo
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            ↓ Exportar
          </button>
        </div>
      </div>

      {/* Two-column shell */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'grid', gridTemplateColumns: '240px 1fr', minHeight: 640 }}>
        {/* Left: field panel */}
        <FieldPanel
          xField={xField}
          yField={yField}
          colorBy={colorBy}
          activeSlot={activeSlot}
          setActiveSlot={setActiveSlot}
          onFieldClick={handleFieldClick}
          showLC={showLC}
          showVC={showVC}
        />

        {/* Right: tabs + content */}
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Tab bar */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--ink-200)', background: 'var(--ink-50)', padding: '0 16px' }}>
            {([
              { k: 'scatter' as const, label: 'Dispersão' },
              { k: 'compare' as const, label: 'Comparador' },
              { k: 'segment' as const, label: 'Segmentador' },
              { k: 'rank'    as const, label: 'Ranking' },
            ]).map(t => (
              <button key={t.k} onClick={() => setTab(t.k)} style={{
                padding: '14px 18px 12px', border: 'none', background: 'transparent',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                color: tab === t.k ? 'var(--brand-700)' : 'var(--ink-600)',
                borderBottom: tab === t.k ? '2px solid var(--brand-500)' : '2px solid transparent',
                marginBottom: -1,
              }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ padding: 20, flex: 1, minWidth: 0, overflowY: 'auto' }} className="fadeIn" key={tab}>
            {tab === 'scatter' && (
              <ScatterTab
                xField={xField} yField={yField} colorBy={colorBy}
                setX={setXField} setY={setYField} setColorBy={setColorBy}
                companies={filteredCompanies}
                onSelect={setSelectedCo}
                selected={selectedCo}
              />
            )}
            {tab === 'compare' && (
              <CompareTab companies={filteredCompanies} showLC={showLC} />
            )}
            {tab === 'segment' && (
              <SegmentTab
                filters={filters}
                setFilters={setFilters}
                companies={filteredCompanies}
                allCount={allCompanies.length}
                showLC={showLC}
              />
            )}
            {tab === 'rank' && (
              <RankTab companies={filteredCompanies} showLC={showLC} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
